import _ from 'lodash';
import {
    CoreFetch,
    PayerApi,
    ProviderCreateClaimProfessionalPayload,
    ProviderFacility,
    ProviderGuarantor,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportCreateClaimProfessionalPayload,
    SupportPayer,
} from '../../../api';

/**
 * The claim data an HL7 (or claim-editor JSON) message carried in, before the worker turned it into a professional
 * record. Provider and support responses name it differently; the claim editor reads it the same way.
 */
export type Hl7ProfessionalPayload = ProviderCreateClaimProfessionalPayload | SupportCreateClaimProfessionalPayload;

export interface IHl7MessageWithPayload {
    created?: string;
    professional?: Hl7ProfessionalPayload;
    providerProfessional?: Hl7ProfessionalPayload;
    supportProfessional?: Hl7ProfessionalPayload;
}

export interface IPendingHl7Snapshot {
    payload: Hl7ProfessionalPayload;
    /** When the message arrived — the moment the displayed professional record fell behind. */
    receivedAt: string;
}

const toTime = (value?: string | Date): number | undefined => {
    if (!value) {
        return undefined;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? undefined : time;
};

/** Same precedence as the claim editor, so the details page and the editor agree on what "latest" means. */
export const extractHl7ProfessionalPayload = (hl7?: IHl7MessageWithPayload): Hl7ProfessionalPayload | undefined =>
    hl7?.providerProfessional ?? hl7?.supportProfessional ?? hl7?.professional;

/**
 * The newest message that carries a professional payload. Messages without one (an ADT^A08 demographics update, a
 * raw message the worker has not parsed) never become a professional record, so they cannot make one look stale.
 * Does not sort in place: the array is usually a store's observable.
 */
export const findLatestHl7Professional = <T extends IHl7MessageWithPayload>(
    hl7s?: T[],
): { hl7: T; payload: Hl7ProfessionalPayload } | undefined => {
    const newestFirst = _.orderBy(hl7s || [], hl7 => toTime(hl7.created) ?? 0, 'desc');
    const hl7 = _.find(newestFirst, message => Boolean(extractHl7ProfessionalPayload(message)));
    const payload = extractHl7ProfessionalPayload(hl7);
    return hl7 && payload ? { hl7, payload } : undefined;
};

/**
 * The HL7 payload to show instead of the displayed professional record, or undefined when the professional record is
 * current. Pending means the newest payload arrived strictly after the professional record was created, or there is
 * no professional record yet. Without a timestamp on either side the order is unknown, and unknown is not reported as
 * pending (NS-3).
 */
export const resolvePendingHl7Snapshot = (
    hl7s: IHl7MessageWithPayload[] | undefined,
    professional: { created?: string } | undefined,
): IPendingHl7Snapshot | undefined => {
    const latest = findLatestHl7Professional(hl7s);
    const receivedTime = toTime(latest?.hl7.created);
    if (!latest || receivedTime === undefined) {
        return undefined;
    }

    if (professional) {
        const professionalTime = toTime(professional.created);
        if (professionalTime === undefined || receivedTime <= professionalTime) {
            return undefined;
        }
    }

    return { payload: latest.payload, receivedAt: latest.hl7.created as string };
};

/** Payer ids the overview needs names for: each subscriber's payer and each third-party administrator. */
export const getHl7PayerIds = (payload: Hl7ProfessionalPayload): string[] =>
    _.uniq(
        _.compact([
            ..._.map(payload.subscribers, 'payerId'),
            payload.primaryThirdPartyAdministratorPayerId,
            payload.secondaryThirdPartyAdministratorPayerId,
            payload.tertiaryThirdPartyAdministratorPayerId,
        ]),
    );

/**
 * Payers by id, through the list endpoint: provider GET /payer/{id} fails for a disabled payer and raises the global
 * error dialog, while the filtered list just leaves it out (the claim editor resolves payers the same way).
 */
export const fetchPayersByIds = async (
    ids: string[],
    app: 'provider' | 'support',
): Promise<Array<ProviderPayer | SupportPayer>> => {
    if (!ids.length) {
        return [];
    }
    const request = { filterId: [ids.map(id => `EQ:${id}`).join(',')], limit: ids.length } as any;
    const api = CoreFetch<PayerApi>(PayerApi);
    const payers = await (app === 'support' ? api.listSupportPayers(request) : api.listProviderPayers(request));
    return payers || [];
};

export interface IHl7ClaimOverview {
    guarantor?: ProviderGuarantor;
    facility?: ProviderFacility;
    subscriberPatient?: ProviderSubscriberPatient;
    payerSubscribers: ProviderPayerSubscriber[];
    payers: ProviderPayer[];
    subscribers: ProviderSubscriber[];
    thirdPartyAdministrators: Array<ProviderPayer | undefined>;
}

/**
 * The payload reshaped into the records `ClaimOverview` renders. A payer not in `payers` (still loading, disabled,
 * or not found) is carried by id alone, so the card can say so instead of claiming there is no payer.
 * Subscribers without an id get a positional one: the overview pairs payer and subscriber by id.
 */
export const buildHl7ClaimOverview = (
    payload: Hl7ProfessionalPayload,
    payers: Array<ProviderPayer | SupportPayer> = [],
): IHl7ClaimOverview => {
    const payerFor = (id?: string): ProviderPayer | undefined => {
        if (!id) {
            return undefined;
        }
        return (_.find(payers, { id }) as ProviderPayer | undefined) || ({ id } as ProviderPayer);
    };

    const subscribers: ProviderSubscriber[] = (payload.subscribers || []).map(
        (subscriber, index) =>
            ({
                id: subscriber.subscriberId || `hl7-subscriber-${index}`,
                first: subscriber.first,
                middle: subscriber.middle,
                last: subscriber.last,
                suffix: subscriber.suffix,
                addresses: subscriber.addresses,
                dob: subscriber.dob,
                phone: subscriber.phone,
                sex: subscriber.sex,
                ssn: subscriber.ssn,
            }) as ProviderSubscriber,
    );

    const payerSubscribers: ProviderPayerSubscriber[] = (payload.subscribers || []).map(
        (subscriber, index) =>
            ({
                memberId: subscriber.memberId,
                payer: payerFor(subscriber.payerId),
                subscriber: subscribers[index],
            }) as ProviderPayerSubscriber,
    );

    const patient = payload.patient;
    const primarySubscriber = _.first(payload.subscribers);
    const subscriberPatient: ProviderSubscriberPatient | undefined = patient
        ? ({
              relationship: primarySubscriber?.relationshipToPatient,
              subscriber: _.first(subscribers),
              patient: {
                  id: patient.patientId,
                  externalId: patient.externalId,
                  nickname: patient.nickname,
                  first: patient.first,
                  middle: patient.middle,
                  last: patient.last,
                  suffix: patient.suffix,
                  addresses: patient.addresses,
                  dob: patient.dob,
                  sex: patient.sex,
                  phones: patient.phones,
                  emails: patient.emails,
              },
          } as ProviderSubscriberPatient)
        : undefined;

    const guarantor = payload.guarantor
        ? ({
              id: payload.guarantor.guarantorId,
              organization: payload.guarantor.organization,
              externalOrganizationId: payload.guarantor.externalOrganizationId,
              first: payload.guarantor.first,
              middle: payload.guarantor.middle,
              last: payload.guarantor.last,
              suffix: payload.guarantor.suffix,
              addresses: payload.guarantor.addresses,
              dob: payload.guarantor.dob,
              phone: payload.guarantor.phone,
              email: payload.guarantor.email,
          } as ProviderGuarantor)
        : undefined;

    const facility = payload.facility
        ? ({
              id: payload.facility.facilityId,
              name: payload.facility.name,
              addresses: payload.facility.addresses || [],
              ein: payload.facility.ein,
              npi: payload.facility.npi,
          } as ProviderFacility)
        : undefined;

    return {
        guarantor,
        facility,
        subscriberPatient,
        payerSubscribers,
        payers: _.compact(_.map(payerSubscribers, 'payer')),
        subscribers,
        thirdPartyAdministrators: [
            payerFor(payload.primaryThirdPartyAdministratorPayerId),
            payerFor(payload.secondaryThirdPartyAdministratorPayerId),
            payerFor(payload.tertiaryThirdPartyAdministratorPayerId),
        ],
    };
};
